import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  TrendingUp, 
  Sparkles, 
  Bot, 
  Layers, 
  ArrowRight, 
  FileText, 
  ShieldCheck, 
  Search, 
  CheckCircle2 
} from "lucide-react";
import AnimatedCounter from "../common/AnimatedCounter";

export default function Hero() {
  const metrics = [
    { label: "Organic Growth", value: "+200%", detail: "Traffic surge at MyOperator", icon: TrendingUp },
    { label: "AI Search Lift", value: "+120%", detail: "AEO / Bing & GSC AI visibility", icon: Bot },
    { label: "Inbound Pipeline", value: "60%", detail: "Company leads driven by SEO", icon: Sparkles },
    { label: "Enterprise Experience", value: "8+ Yrs", detail: "Global B2B SaaS & Migrations", icon: Layers },
  ];

  return (
    <section 
      id="overview" 
      className="relative min-h-screen pt-28 pb-16 lg:pt-36 lg:pb-24 overflow-hidden bg-[#070b12] bg-grid-mesh flex items-center"
    >
      {/* Ambient background glow accents with smooth breathing animation */}
      <motion.div 
        animate={{ scale: [1, 1.08, 1], opacity: [0.18, 0.28, 0.18] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[400px] pointer-events-none"
        style={{ background: "radial-gradient(ellipse at center, rgba(79, 70, 229, 0.22) 0%, rgba(79, 70, 229, 0) 70%)" }}
      />
      <motion.div 
        animate={{ scale: [1, 1.12, 1], opacity: [0.12, 0.22, 0.12] }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 1 }}
        className="absolute top-1/3 -left-40 w-[500px] h-[500px] pointer-events-none"
        style={{ background: "radial-gradient(circle at center, rgba(6, 182, 212, 0.18) 0%, rgba(6, 182, 212, 0) 70%)" }}
      />
      <motion.div 
        animate={{ scale: [1, 1.06, 1], opacity: [0.1, 0.2, 0.1] }}
        transition={{ duration: 9, repeat: Infinity, ease: "easeInOut", delay: 2 }}
        className="absolute bottom-10 right-0 w-[550px] h-[450px] pointer-events-none"
        style={{ background: "radial-gradient(ellipse at center, rgba(99, 102, 241, 0.15) 0%, rgba(99, 102, 241, 0) 70%)" }}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          
          {/* Left Column: Headline, Bio, Metrics, CTAs */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-7 flex flex-col items-start"
          >
            {/* Live Availability Badge */}
            <div className="inline-flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3.5 py-1 sm:py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 backdrop-blur-md mb-6 hover:border-emerald-400/50 transition-colors max-w-full">
              <span className="relative flex h-2 w-2 flex-shrink-0">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
              </span>
              <span className="text-[10px] sm:text-xs font-mono uppercase tracking-tight sm:tracking-wider text-emerald-300 font-semibold whitespace-nowrap">
                Open to High-Impact B2B SaaS Roles & Consulting
              </span>
            </div>

            {/* Giant Title / Branding */}
            <h1 className="font-heading font-black text-4xl sm:text-6xl lg:text-7xl tracking-tighter text-white uppercase leading-[0.95] mb-4">
              KANNA NARENDRA <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#818cf8] via-[#38bdf8] to-[#34d399]">
                KUMAR
              </span>
            </h1>

            {/* Sub-headline / Core Positioning */}
            <p className="text-lg sm:text-xl font-semibold text-white/95 mb-4 tracking-tight flex items-center gap-2 flex-wrap">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#38bdf8] via-[#818cf8] to-[#34d399] font-bold">
                AEO/GEO & SEO Consultant
              </span>
            </p>

            <p className="text-xs sm:text-base text-white/70 leading-relaxed max-w-2xl mb-6 sm:mb-8">
              8+ years scaling organic visibility and high-intent inbound pipeline for global B2B SaaS. 
              Pioneered <span className="text-white font-medium">Hybrid Engine Optimization (HEO)</span> to capture high-converting search demand across both traditional search engines (Google, Bing) and LLM-powered answer platforms (ChatGPT, Claude, Gemini, and Google AI Overviews).
            </p>

            {/* Hand-drawn note - Warm Eye-Pleasing Amber Accent */}
            <div className="inline-flex items-center gap-2 sm:gap-3 mb-8 bg-amber-500/[0.08] border border-amber-500/25 px-3 sm:px-4 py-2 sm:py-2.5 rounded-xl shadow-sm max-w-full">
              <span className="font-handwriting text-[15px] sm:text-xl md:text-2xl text-amber-300 font-bold whitespace-nowrap">
                Specialized in AI Visibility & Technical SEO!
              </span>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-wrap items-center gap-3 sm:gap-4 mb-10 w-full sm:w-auto">
              <Link
                to="/ai-visibility-audit/"
                className="px-6 py-3.5 rounded-xl bg-gradient-to-r from-blue-600 via-indigo-600 to-cyan-500 hover:from-blue-500 hover:to-cyan-400 text-white font-heading font-bold text-sm tracking-wide shadow-[0_0_25px_rgba(6,182,212,0.45)] hover:shadow-[0_0_35px_rgba(6,182,212,0.65)] flex items-center justify-center gap-2.5 transition-all group border border-cyan-400/40"
              >
                <Sparkles className="w-4 h-4 text-cyan-200 group-hover:rotate-12 transition-transform" />
                <span>AI Visibility Audit</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </Link>

              <motion.a
                whileHover={{ y: -3, scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                href="/#case-studies"
                className="flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl bg-white/[0.06] hover:bg-white/[0.12] text-white font-semibold text-sm border border-white/[0.15] backdrop-blur-md transition-all duration-200"
              >
                <span>Case Studies</span>
              </motion.a>

              <motion.a
                whileHover={{ y: -3, scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                href="/#contact"
                className="flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl bg-white/[0.04] hover:bg-white/[0.08] text-white/90 hover:text-white font-semibold text-sm border border-white/[0.10] backdrop-blur-md transition-all duration-200"
              >
                <span>Get In Touch</span>
              </motion.a>

              <motion.a
                whileHover={{ y: -3, scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                href="/Narendra-Resume-2026.pdf"
                download="Kanna_Narendra_Kumar_Resume.pdf"
                className="flex items-center justify-center gap-2 px-4 py-3.5 rounded-xl bg-white/[0.03] hover:bg-white/[0.07] text-white/75 hover:text-white font-semibold text-sm border border-white/[0.08] backdrop-blur-md transition-all duration-200"
              >
                <FileText className="w-4 h-4 text-[#38bdf8]" />
                <span>Resume</span>
              </motion.a>
            </div>

            {/* Quick Skills Badges: Fixed Steady "Core Tech:" Label + Smooth Single-Line Ticker */}
            <div className="pt-2 border-t border-white/[0.08] w-full">
              <div className="flex items-center w-full py-1 relative">
                {/* Fixed Steady "Core Tech:" Label */}
                <div className="flex-shrink-0 z-20 bg-[#070b14] pr-3 flex items-center shadow-[6px_0_12px_#070b14]">
                  <span className="text-xs font-mono uppercase tracking-wider text-white/50 whitespace-nowrap font-semibold">
                    Core Tech:
                  </span>
                </div>

                {/* Auto-scrolling skills ticker in a single line */}
                <div className="relative overflow-hidden w-full flex items-center select-none">
                  {/* Subtle left fade after label */}
                  <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-3 bg-gradient-to-r from-[#070b14] to-transparent z-10" />

                  <div className="animate-skills-marquee flex items-center gap-2 w-max py-0.5">
                    {[
                      "Technical SEO",
                      "Basic WordPress",
                      "AEO & GEO",
                      "HEO Framework",
                      "Google AI Overviews",
                      "GA4 & GSC",
                      "Screaming Frog",
                      "Technical SEO",
                      "Basic WordPress",
                      "AEO & GEO",
                      "HEO Framework",
                      "Google AI Overviews",
                      "GA4 & GSC",
                      "Screaming Frog"
                    ].map((tag, idx) => (
                      <span 
                        key={idx}
                        className="text-[11px] font-mono font-medium px-2.5 py-1 rounded-md bg-white/[0.04] border border-white/[0.06] text-white/70 hover:text-[#38bdf8] hover:border-[#06b6d4]/40 hover:bg-white/[0.08] whitespace-nowrap flex-shrink-0 transition-all cursor-default"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  {/* Soft edge fade on the right */}
                  <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-[#070b14] to-transparent z-10" />
                </div>
              </div>
            </div>

          </motion.div>

          {/* Right Column: Framed Persona Visual & Floating Tech Pills */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-5 relative flex justify-center items-center"
          >
            {/* Background Glow */}
            <div className="absolute inset-0 bg-gradient-to-tr from-[#4f46e5]/30 via-transparent to-[#06b6d4]/20 rounded-3xl blur-2xl transform rotate-3 scale-95" />

            {/* Main Portrait Card */}
            <div className="relative w-full max-w-md bg-[#0c101c]/90 border border-white/10 rounded-3xl p-4 sm:p-6 shadow-2xl shadow-black/80 backdrop-blur-xl">
              
              {/* Image Frame */}
              <div className="relative rounded-2xl overflow-hidden aspect-[4/4.8] bg-[#121827] border border-white/[0.08] group">
                <picture>
                  <source srcSet="/narendra-hero.webp" type="image/webp" />
                  <img 
                    src="/narendra-hero.jpg" 
                    alt="Kanna Narendra Kumar - SEO & AI Search Strategist" 
                    fetchPriority="high"
                    decoding="sync"
                    className="w-full h-full object-cover object-[center_12%] transition-transform duration-700 group-hover:scale-105 filter brightness-95 contrast-105"
                  />
                </picture>
                <div className="absolute inset-0 bg-gradient-to-t from-[#090d16] via-transparent to-transparent opacity-80" />

                {/* Bottom Overlay on Image */}
                <div className="absolute bottom-4 left-4 right-4 p-3 rounded-xl bg-black/60 backdrop-blur-md border border-white/10">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white font-heading font-bold text-sm">Kanna Narendra Kumar</p>
                      <p className="text-[#38bdf8] text-xs font-mono">AEO/GEO &amp; SEO Consultant</p>
                    </div>
                    <div className="w-8 h-8 rounded-lg bg-[#06b6d4]/20 border border-[#06b6d4]/40 flex items-center justify-center text-[#38bdf8]">
                      <Search className="w-4 h-4" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating Badge 1: Top Left */}
              <motion.div 
                animate={{ y: [0, -6, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -top-4 -left-4 sm:-left-6 bg-[#0f1526]/95 border border-emerald-500/30 px-3.5 py-2 rounded-xl shadow-xl backdrop-blur-md flex items-center gap-2.5"
              >
                <div className="w-6 h-6 rounded-lg bg-emerald-500/20 text-emerald-400 flex items-center justify-center">
                  <TrendingUp className="w-3.5 h-3.5" />
                </div>
                <div className="text-left">
                  <p className="text-[10px] uppercase font-mono text-emerald-400 tracking-wider font-semibold">Organic Scale</p>
                  <p className="text-xs font-bold text-white">200% Organic Traffic Growth</p>
                </div>
              </motion.div>

              {/* Floating Badge 2: Right Middle */}
              <motion.div 
                animate={{ y: [0, 8, 0] }}
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                className="absolute top-1/2 -right-4 sm:-right-8 bg-[#0f1526]/95 border border-[#6366f1]/40 px-3.5 py-2.5 rounded-xl shadow-2xl backdrop-blur-md flex items-center gap-2.5"
              >
                <div className="w-7 h-7 rounded-lg bg-[#6366f1]/20 text-[#818cf8] flex items-center justify-center">
                  <Bot className="w-4 h-4" />
                </div>
                <div className="text-left">
                  <p className="text-[10px] uppercase font-mono text-[#818cf8] tracking-wider font-semibold">AI Search</p>
                  <p className="text-xs font-bold text-white">+120% AI Visibility</p>
                </div>
              </motion.div>

              {/* Floating Badge 3: Bottom Left */}
              <motion.div 
                animate={{ y: [0, -5, 0] }}
                transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
                className="absolute -bottom-4 -left-3 sm:-left-4 bg-[#0f1526]/95 border border-white/15 px-3 py-2 rounded-xl shadow-xl backdrop-blur-md flex items-center gap-2"
              >
                <div className="w-6 h-6 rounded-lg bg-cyan-500/20 text-cyan-400 flex items-center justify-center">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                </div>
                <div>
                  <p className="text-xs font-bold text-white">60% Inbound Leads</p>
                  <p className="text-[9px] font-mono text-white/50">MyOperator Growth</p>
                </div>
              </motion.div>

            </div>
          </motion.div>

        </div>

        {/* Metrics Grid Bar */}
        <div 
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-16 sm:mt-20 pt-8 border-t border-white/[0.08]"
        >
          {metrics.map((item, idx) => {
            const Icon = item.icon;
            return (
              <motion.div 
                key={idx}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.3 + idx * 0.12 }}
                whileHover={{ y: -4, borderColor: "rgba(99, 102, 241, 0.5)" }}
                className="p-5 rounded-2xl bg-[#0d1220]/70 border border-white/[0.06] hover:bg-[#11172a] transition-all duration-300 group cursor-default"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-mono uppercase tracking-wider text-white/50">{item.label}</span>
                  <div className="p-1.5 rounded-lg bg-white/[0.04] text-white/40 group-hover:text-[#38bdf8] group-hover:bg-[#06b6d4]/10 transition-colors">
                    <Icon className="w-4 h-4" />
                  </div>
                </div>
                <div className="text-2xl sm:text-3xl font-heading font-black text-white group-hover:text-[#38bdf8] transition-colors">
                  <AnimatedCounter value={item.value} duration={3.4} delay={0.35 + idx * 0.12} />
                </div>
                <p className="text-xs text-white/60 mt-1 font-medium">{item.detail}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
